import { useEffect, useState, useCallback } from "react";
import { create, type StateCreator, type StoreApi, type UseBoundStore } from "zustand";
import { wsClient } from "./ws-client";
import { useConnectionStore } from "../stores/connection";

type TopicFn<S> = string | ((state: S) => string | null);
type ParamsFn<S> = (state: S) => unknown;

interface LiveStoreConfig<TData, TExtra> {
  topic: TopicFn<TData & TExtra & LiveMeta>;
  params?: ParamsFn<TData & TExtra & LiveMeta>;
  onSnapshot: (data: unknown, state: TData & TExtra & LiveMeta) => Partial<TData>;
  onEvent?: (event: unknown, state: TData & TExtra & LiveMeta) => Partial<TData> | null;
  state: (set: StoreApi<TData & TExtra & LiveMeta>["setState"], get: StoreApi<TData & TExtra & LiveMeta>["getState"]) => TExtra;
}

interface LiveMeta {
  _loading: boolean;
  _error: string | null;
}

type LiveStore<TData, TExtra> = UseBoundStore<StoreApi<TData & TExtra & LiveMeta>> & {
  live: (overrides?: Partial<TData & TExtra>) => void;
};

export function createLiveStore<TData, TExtra = {}>(
  config: LiveStoreConfig<TData, TExtra>
): LiveStore<TData, TExtra> {
  type S = TData & TExtra & LiveMeta;

  const useStore = create<S>()((set, get) => ({
    ...(config.state(set as any, get as any) as any),
    _loading: true,
    _error: null,
  })) as UseBoundStore<StoreApi<S>>;

  let subId: string | null = null;
  let refCount = 0;

  function resolveTopic(state: S): string | null {
    return typeof config.topic === "function" ? config.topic(state) : config.topic;
  }

  function subscribe() {
    const state = useStore.getState();
    const topic = resolveTopic(state);
    if (!topic || subId) return;

    const params = config.params?.(state);
    subId = wsClient.subscribe(
      topic,
      (data, isSnapshot) => {
        if (isSnapshot) {
          const updates = config.onSnapshot(data, useStore.getState());
          useStore.setState({ ...updates, _loading: false, _error: null } as Partial<S>);
        } else if (config.onEvent) {
          const updates = config.onEvent(data, useStore.getState());
          if (updates === null) {
            wsClient.resubscribe(topic, config.params?.(useStore.getState()));
          } else {
            useStore.setState(updates as Partial<S>);
          }
        }
      },
      params
    );
  }

  function unsubscribe() {
    if (subId) {
      wsClient.unsubscribe(subId);
      subId = null;
    }
  }

  useStore.subscribe((state, prevState) => {
    if (refCount === 0) return;

    const newTopic = resolveTopic(state);
    const oldTopic = resolveTopic(prevState);
    const newParams = config.params ? JSON.stringify(config.params(state)) : null;
    const oldParams = config.params ? JSON.stringify(config.params(prevState)) : null;

    if (newTopic !== oldTopic || newParams !== oldParams) {
      unsubscribe();
      if (!useStore.getState()._loading) {
        useStore.setState({ _loading: true } as Partial<S>);
      }
      if (newTopic) subscribe();
    }
  });

  useConnectionStore.subscribe((conn, prevConn) => {
    if (conn.status === "connected" && prevConn.status !== "connected" && refCount > 0 && !subId) {
      subscribe();
    }
    if (conn.status !== "connected" && prevConn.status === "connected") {
      subId = null;
    }
  });

  const live = function useLive(overrides?: Partial<TData & TExtra>) {
    useEffect(() => {
      if (overrides) {
        const current = useStore.getState();
        const needsUpdate = Object.entries(overrides).some(
          ([k, v]) => (current as Record<string, unknown>)[k] !== v
        );
        if (needsUpdate) useStore.setState(overrides as Partial<S>);
      }
      refCount++;
      if (refCount === 1 && useConnectionStore.getState().status === "connected") {
        subscribe();
      }
      return () => {
        refCount--;
        if (refCount === 0) {
          unsubscribe();
          useStore.setState({ _loading: true } as Partial<S>);
        }
      };
    }, []);
  };

  (useStore as LiveStore<TData, TExtra>).live = live;
  return useStore as LiveStore<TData, TExtra>;
}

export function rpc<T = unknown>(method: string, params?: unknown): Promise<T> {
  return wsClient.rpc<T>(method, params);
}

export function useRpc(method: string) {
  const [isPending, setIsPending] = useState(false);
  const connected = useConnectionStore((s) => s.status === "connected");

  const mutate = useCallback(
    async (params?: unknown) => {
      setIsPending(true);
      try {
        return await wsClient.rpc(method, params);
      } finally {
        setIsPending(false);
      }
    },
    [method]
  );

  return { mutate, mutateAsync: mutate, isPending, isConnected: connected };
}
